import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuCheck, LuRefreshCw, LuSearch, LuUserPlus, LuX } from "react-icons/lu";
import * as styled from "./AddStudent.styled.jsx";
import axios from "../../../axios";
import TopPageTitle from "../../../components/TopPageTitle/TopPageTitle.jsx";
import Loader from "../../../components/Loader/Loader.jsx";
import { PagesWrapper } from "../../../styles/components/PagesWrapper.styled";
import { useAdmin } from "../../../context/AdminInfo.js";
import { secondStepFields, secondStepFieldsDE } from "../../../helpers/studentFields.js";
import { getAllTeachers } from "../../../helpers/getAllTeachers";
import { getAllManagers } from "../../../helpers/getAllManagers";
import { courseOf, fetchPlatformCourses } from "../../../helpers/materials/platformLessons";
import MaterialsSourceSwitch from "../../../components/MaterialsSourceSwitch/MaterialsSourceSwitch.jsx";

// Форма «Додати учня»: одна сторінка замість трьох кроків. Поля й запити ті самі, що були:
// POST students → вчителю в список → менеджеру в список → пробний урок вчителю (якщо обрано).
const DAYS = [
  { name: "Пн", value: "monday" },
  { name: "Вт", value: "tuesday" },
  { name: "Ср", value: "wednesday" },
  { name: "Чт", value: "thursday" },
  { name: "Пт", value: "friday" },
  { name: "Сб", value: "saturday" },
  { name: "Нд", value: "sunday" },
];

const EMPTY = {
  name: "",
  studentId: "",
  phoneNumber: "",
  email: "",
  password: "",
  lessonPrice: "",
  ageGroup: "",
  englishLevel: "",
  lessonPackage: "",
  materialsSource: "canva",
};

const generatePassword = () => {
  const chars = "abcdefghjkmnpqrstuvwxyz23456789";
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
};

// Пошук і вибір вчителя / менеджера зі списку
function PersonPicker({ label, people, value, onChange, error, optional, idKey = "teacherId" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = people.find((person) => person._id === value);
  const filtered = people.filter((person) =>
    `${person.name} ${person[idKey] || ""}`.toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <styled.Field>
      <styled.Label>
        {label}
        {optional && <styled.Optional>необовʼязково</styled.Optional>}
      </styled.Label>
      <styled.PickerButton type="button" $open={open} $error={!!error} onClick={() => setOpen((prev) => !prev)}>
        {selected ? (
          <span>
            {selected.name} <small>id {selected[idKey]}</small>
          </span>
        ) : (
          <styled.Placeholder>Оберіть зі списку</styled.Placeholder>
        )}
        {selected && optional && (
          <styled.Clear
            onClick={(event) => {
              event.stopPropagation();
              onChange("");
            }}
          >
            <LuX size={16} />
          </styled.Clear>
        )}
      </styled.PickerButton>
      {open && (
        <styled.PickerList>
          <styled.Search>
            <LuSearch size={16} />
            <input autoFocus placeholder="Пошук за імʼям або id" value={query} onChange={(event) => setQuery(event.target.value)} />
          </styled.Search>
          {filtered.map((person) => (
            <styled.PickerItem
              key={person._id}
              type="button"
              $active={person._id === value}
              onClick={() => {
                onChange(person._id);
                setOpen(false);
                setQuery("");
              }}
            >
              <span>{person.name}</span>
              <small>id {person[idKey]}</small>
            </styled.PickerItem>
          ))}
          {filtered.length === 0 && <styled.Empty>Нікого не знайдено</styled.Empty>}
        </styled.PickerList>
      )}
      {error && <styled.Error>{error}</styled.Error>}
    </styled.Field>
  );
}

function AddStudent() {
  const { adminInfo } = useAdmin();
  const navigate = useNavigate();
  const accessType = adminInfo?.accessType;
  const fields = accessType === "en" ? secondStepFields : secondStepFieldsDE;
  const groupOptions = fields.find(({ formName }) => formName === "ageGroup").selectOptions;
  const packageOptions = fields.find(({ formName }) => formName === "lessonPackage").selectOptions;
  const fallbackLevels = fields.find(({ formName }) => formName === "englishLevel").selectOptions;

  const [form, setForm] = useState(EMPTY);
  const [schedule, setSchedule] = useState({}); // { monday: "18:00" }
  const [teacherId, setTeacherId] = useState("");
  const [trialTeacherId, setTrialTeacherId] = useState("");
  const [managerId, setManagerId] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [levels, setLevels] = useState(null);
  const [platformCourses, setPlatformCourses] = useState([]);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | saving | done | error
  const [savedName, setSavedName] = useState("");

  useEffect(() => {
    if (!accessType) return;
    getAllTeachers().then((list) => setTeachers(list.filter((teacher) => teacher.accessType === accessType))).catch(() => {});
    getAllManagers().then((list) => setManagers(list.filter((manager) => manager.accessType === accessType))).catch(() => {});
    fetchPlatformCourses().then(setPlatformCourses);
  }, [accessType]);

  // Рівні — ті, для яких у групи є матеріали (колекція levels); якщо не вдалося — повний список
  useEffect(() => {
    if (!form.ageGroup || !accessType) return;
    setLevels(null);
    axios
      .get(`/levels/${form.ageGroup}/${accessType}`)
      .then(({ data }) => setLevels(data.levels.map((level) => ({ value: level, name: level }))))
      .catch(() => setLevels(fallbackLevels));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.ageGroup, accessType]);

  const hasPlatform = accessType === "en" && platformCourses.includes(courseOf(form.ageGroup, form.englishLevel));

  // Новим учням рівня, де вже є уроки платформи, за замовчуванням — платформа
  useEffect(() => {
    setForm((prev) => ({ ...prev, materialsSource: hasPlatform ? "platform" : "canva" }));
  }, [hasPlatform]);

  const set = (key, value) => {
    // Інша група — інші рівні: скидаємо рівень
    setForm((prev) => ({ ...prev, [key]: value, ...(key === "ageGroup" && prev.ageGroup !== value ? { englishLevel: "" } : {}) }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const teacher = useMemo(() => teachers.find(({ _id }) => _id === teacherId), [teachers, teacherId]);

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Вкажіть імʼя";
    if (!form.studentId.trim()) next.studentId = "Вкажіть id учня";
    if (form.phoneNumber.replace(/\D/g, "").length < 5) next.phoneNumber = "Вкажіть номер телефону";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) next.email = "Невірний email";
    if (form.password.length < 4) next.password = "Мінімум 4 символи";
    if (!form.lessonPrice.trim()) next.lessonPrice = "Вкажіть ціну";
    if (!form.ageGroup) next.ageGroup = "Оберіть групу";
    if (!form.englishLevel) next.englishLevel = "Оберіть рівень";
    if (!form.lessonPackage) next.lessonPackage = "Оберіть пакет";
    if (!teacherId) next.teacherId = "Оберіть вчителя";
    if (Object.values(schedule).some((time) => !time)) next.schedule = "Вкажіть час для кожного обраного дня";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setStatus("saving");
    const classSchedule = DAYS.filter(({ value }) => schedule[value]).map(({ value }) => ({ day: value, time: schedule[value] }));
    const data = {
      ...form,
      name: form.name.trim(),
      studentId: form.studentId.trim(),
      email: form.email.trim(),
      materialsSource: hasPlatform ? form.materialsSource : "canva",
      classSchedule,
      teacherId,
      teacherName: teacher?.name || "",
      accessType,
      userType: "student",
    };
    try {
      const { data: student } = await axios.post("students", data);
      const { name, ageGroup, englishLevel, studentId, materialsSource } = data;
      await axios
        .post(`teachers/${teacherId}/students`, { name, ageGroup, _id: student._id, classSchedule, englishLevel, studentId, accessType, materialsSource })
        .catch((error) => console.error("Error adding student to teacher:", error));
      if (managerId) {
        await axios
          .post(`manager/${managerId}/students`, { name, _id: student._id, studentId, accessType })
          .catch((error) => console.error("Error adding student to manager:", error));
      }
      if (trialTeacherId) {
        await axios
          .post("trial-lesson/mark", { teacherId: trialTeacherId, salary: "100", lessonType: "Trial" })
          .catch((error) => console.error("Error mark trial lesson:", error));
      }
      setSavedName(data.name);
      setStatus("done");
    } catch (error) {
      console.error("Error adding student:", error);
      setStatus("error");
    }
  };

  const reset = () => {
    setForm(EMPTY);
    setSchedule({});
    setTeacherId("");
    setTrialTeacherId("");
    setManagerId("");
    setErrors({});
    setStatus("idle");
  };

  if (!adminInfo) return <Loader />;

  if (status === "done") {
    return (
      <PagesWrapper>
        <styled.Done>
          <styled.DoneIcon>
            <LuCheck size={32} />
          </styled.DoneIcon>
          <h2>Учня {savedName} додано</h2>
          <p>Логін і пароль можна передати учню — він уже може увійти в кабінет.</p>
          <styled.Actions>
            <styled.Secondary type="button" onClick={() => navigate("/admin/ourstudents")}>
              До наших учнів
            </styled.Secondary>
            <styled.Primary type="button" onClick={reset}>
              Додати ще одного
            </styled.Primary>
          </styled.Actions>
        </styled.Done>
      </PagesWrapper>
    );
  }

  const text = (key, label, props = {}) => (
    <styled.Field>
      <styled.Label>{label}</styled.Label>
      <styled.Input value={form[key]} $error={!!errors[key]} onChange={(event) => set(key, event.target.value)} {...props} />
      {errors[key] && <styled.Error>{errors[key]}</styled.Error>}
    </styled.Field>
  );

  const chips = (key, options) => (
    <styled.Chips>
      {options.map(({ name, value }) => (
        <styled.Chip key={value} type="button" $active={form[key] === value} onClick={() => set(key, value)}>
          {name}
        </styled.Chip>
      ))}
    </styled.Chips>
  );

  return (
    <PagesWrapper>
      <TopPageTitle value="Додати учня" />
      <styled.Form onSubmit={onSubmit} noValidate>
        <styled.Section>
          <styled.SectionTitle>
            <span>1</span> Учень
          </styled.SectionTitle>
          <styled.Grid>
            {text("name", "Імʼя та прізвище", { placeholder: "Олена Коваль" })}
            {text("studentId", "Id учня", { placeholder: "напр. 245" })}
            {text("phoneNumber", "Телефон", { placeholder: "+380…", type: "tel" })}
            {text("email", "Email (логін)", { placeholder: "name@speaksy.com", type: "email", autoComplete: "off" })}
            <styled.Field>
              <styled.Label>Пароль</styled.Label>
              <styled.InputRow>
                <styled.Input value={form.password} $error={!!errors.password} autoComplete="new-password" onChange={(event) => set("password", event.target.value)} />
                <styled.IconButton type="button" title="Згенерувати" onClick={() => set("password", generatePassword())}>
                  <LuRefreshCw size={18} />
                </styled.IconButton>
              </styled.InputRow>
              {errors.password && <styled.Error>{errors.password}</styled.Error>}
            </styled.Field>
          </styled.Grid>
        </styled.Section>

        <styled.Section>
          <styled.SectionTitle>
            <span>2</span> Навчання
          </styled.SectionTitle>
          <styled.Field>
            <styled.Label>Вікова група</styled.Label>
            {chips("ageGroup", groupOptions)}
            {errors.ageGroup && <styled.Error>{errors.ageGroup}</styled.Error>}
          </styled.Field>
          <styled.Field>
            <styled.Label>Рівень</styled.Label>
            {!form.ageGroup ? (
              <styled.Placeholder>Спочатку оберіть групу</styled.Placeholder>
            ) : levels === null ? (
              <styled.Placeholder>Завантаження…</styled.Placeholder>
            ) : (
              chips("englishLevel", levels)
            )}
            {errors.englishLevel && <styled.Error>{errors.englishLevel}</styled.Error>}
          </styled.Field>
          {accessType === "en" && form.englishLevel && (
            <styled.Field>
              <styled.Label>Матеріали</styled.Label>
              <MaterialsSourceSwitch label="" showHint value={form.materialsSource} hasPlatform={hasPlatform} onChange={(value) => set("materialsSource", value)} />
            </styled.Field>
          )}
          <styled.Grid>
            <styled.Field>
              <styled.Label>Пакет уроків</styled.Label>
              {chips("lessonPackage", packageOptions)}
              {errors.lessonPackage && <styled.Error>{errors.lessonPackage}</styled.Error>}
            </styled.Field>
            {text("lessonPrice", "Ціна за урок, грн", { placeholder: "напр. 350", inputMode: "numeric" })}
          </styled.Grid>
        </styled.Section>

        <styled.Section>
          <styled.SectionTitle>
            <span>3</span> Вчителі
          </styled.SectionTitle>
          <styled.Grid>
            <PersonPicker label="Вчитель" people={teachers} value={teacherId} error={errors.teacherId} onChange={(id) => { setTeacherId(id); setErrors((prev) => ({ ...prev, teacherId: undefined })); }} />
            <PersonPicker label="Менеджер" optional people={managers} value={managerId} idKey="managerId" onChange={setManagerId} />
            <PersonPicker label="Вчитель пробного уроку" optional people={teachers} value={trialTeacherId} onChange={setTrialTeacherId} />
          </styled.Grid>
        </styled.Section>

        <styled.Section>
          <styled.SectionTitle>
            <span>4</span> Розклад
          </styled.SectionTitle>
          <styled.Chips>
            {DAYS.map(({ name, value }) => (
              <styled.Chip
                key={value}
                type="button"
                $active={value in schedule}
                onClick={() => {
                  setSchedule((prev) => {
                    const next = { ...prev };
                    if (value in next) delete next[value];
                    else next[value] = "";
                    return next;
                  });
                  setErrors((prev) => ({ ...prev, schedule: undefined }));
                }}
              >
                {name}
              </styled.Chip>
            ))}
          </styled.Chips>
          {DAYS.filter(({ value }) => value in schedule).map(({ name, value }) => (
            <styled.DayRow key={value}>
              <b>{name}</b>
              <styled.Input
                type="time"
                value={schedule[value]}
                $error={!!errors.schedule && !schedule[value]}
                onChange={(event) => setSchedule((prev) => ({ ...prev, [value]: event.target.value }))}
              />
            </styled.DayRow>
          ))}
          {Object.keys(schedule).length === 0 && <styled.Placeholder>Оберіть дні занять (можна додати пізніше)</styled.Placeholder>}
          {errors.schedule && <styled.Error>{errors.schedule}</styled.Error>}
        </styled.Section>

        {Object.values(errors).some(Boolean) && <styled.Alert>Перевірте поля, позначені червоним</styled.Alert>}
        {status === "error" && <styled.Alert>Не вдалося додати учня. Можливо, такий email уже існує — перевірте дані й спробуйте ще раз.</styled.Alert>}

        <styled.Primary type="submit" disabled={status === "saving"}>
          <LuUserPlus size={20} />
          {status === "saving" ? "Додаємо…" : "Додати учня"}
        </styled.Primary>
      </styled.Form>
    </PagesWrapper>
  );
}

export default AddStudent;
